import React, { useCallback, useState } from 'react'

import { Box } from '@mui/material'

import Button from '../../../../shared/components/Button/Button.component'
import { Chip } from '../../../../shared/components/Chip/Chip.component'
import UrlWidget from '../../../../shared/components/UrlWidget/UrlWidget.component'
import MiniTripDetails from '../../../SharedTrip/components/MiniTripDetails/MiniTripDetails.component'

import { getItemDetails } from '../../../../redux/states/items/getItemDetails/getItemDetails.slice'
import router from '../../../../router/functions/router.functions'
import routes from '../../../../router/routes.dictionary'
import Snackbar from '../../../../shared/components/Snackbar/Snackbar.functions'
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/redux.hooks'
import useResponsiveness from '../../../../shared/hooks/responsive.hooks'
import { isValidMarketplaceUrl } from './MarketplaceLinksInput.functions'
import styles from './MarketplaceLinksInput.styles'
import { MarketplaceLinksInputProps, MarketplaceUrlError } from './MarketplaceLinksInput.types'

const extractAmazonDomain = (url: string): string | null => {
  try {
    const urlObj = new URL(url)

    if (urlObj.hostname.includes('amazon.') || urlObj.hostname.includes('a.')) {
      return urlObj.hostname
    }

    return null
  } catch {
    return null
  }
}

export const MarketplaceLinksInput: React.FC<MarketplaceLinksInputProps> = (props) => {
  const { flightItinerary, isCompact = false } = props

  const [errors, setErrors] = useState<MarketplaceUrlError[]>([])
  const [urls, setUrls] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isDesktop] = useResponsiveness()

  const { loading, success: itemDetails } = useAppSelector((state) => state.getItemDetails)

  const dispatch = useAppDispatch()
  const navigate = router.navigate()

  const handleErrorUpdate = useCallback(
    (newError: MarketplaceUrlError) => {
      // Only add domain errors once
      if (newError.error.type === 'domain') {
        const domainErrorExists = errors.some((error) => error.error.type === 'domain' && error.url === newError.url)

        if (!domainErrorExists) {
          setErrors((prevErrors) => [...prevErrors, newError])
        }
      } else {
        // Handle other errors as before
        const errorExists = errors.some((error) => error.url === newError.url)

        if (!errorExists) {
          setErrors((prevErrors) => [...prevErrors, newError])
        }
      }
    },
    [errors]
  )

  const validateDomainConsistency = useCallback((urlList: string[]): boolean => {
    if (urlList.length === 0) return true

    const domains = urlList.map(extractAmazonDomain).filter((domain): domain is string => domain !== null)

    if (domains.length !== urlList.length) return false

    const firstDomain = domains[0]

    return domains.every((domain) => domain === firstDomain)
  }, [])

  const handleUrlValidation = useCallback(
    (url: string) => {
      const basicValidation = isValidMarketplaceUrl(url, handleErrorUpdate, flightItinerary)

      // Only check domain consistency if the URL passes basic validation
      if (basicValidation) {
        const allUrls = [...(itemDetails?.data?.processedItems?.map((item) => item.internalUrl) ?? []), ...urls, url]
        const domainsMatch = validateDomainConsistency(
          allUrls.filter((url) => isValidMarketplaceUrl(url, undefined, flightItinerary))
        )

        if (!domainsMatch) {
          handleErrorUpdate({
            url,
            error: {
              message: 'All Amazon links must be from the same country.',
              type: 'domain'
            }
          })

          return false
        }
      }

      return basicValidation
    },
    [handleErrorUpdate, flightItinerary, urls, validateDomainConsistency, itemDetails]
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleInputKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      addUrl()
    }
  }

  const addUrl = () => {
    if (inputValue.trim()) {
      setUrls((prevUrls) => [...prevUrls, inputValue.trim()])
      setInputValue('')
    }
  }

  const removeUrl = (urlToRemove: string) => {
    setUrls((prevUrls) => prevUrls.filter((url) => url !== urlToRemove))

    setErrors((prevErrors) => {
      const remainingUrls = urls.filter((url) => url !== urlToRemove)
      const domainValid = validateDomainConsistency(remainingUrls)

      // Remove both domain errors and specific URL errors
      return prevErrors
        .filter((error) => error.url !== urlToRemove) // Remove errors for deleted URL
        .filter((error) => !domainValid || error.error.type !== 'domain') // Remove domain errors if domains now valid
    })
  }

  const onSubmit = () => {
    setErrors([])

    const validUrls = urls.filter((url) => handleUrlValidation(url))

    dispatch(
      getItemDetails({
        request: {
          urls: validUrls
        },

        onSuccess: (results) => {
          const { processedItems, unprocessableItems } = results
          const onlyUnprocessableItems = processedItems.length === 0 && unprocessableItems.length >= 0

          if (onlyUnprocessableItems) {
            const hasUnavailableItems = unprocessableItems.some((item) => item.error?.details?.includes('unavailable'))

            if (hasUnavailableItems) {
              Snackbar.show({
                message:
                  unprocessableItems.length === 1
                    ? 'This item may be unavailable. Check it on Amazon and try again.'
                    : 'Some items may be unavailable. Check them on Amazon and try again.',
                severity: 'warning'
              })
            } else {
              Snackbar.show({
                message: 'There was an issue processing your request. Please try again shortly.',
                severity: 'error'
              })
            }
          } else {
            navigate(routes.cart.path, { state: { flightItineraryId: flightItinerary?.id } })
            setUrls([])

            if (unprocessableItems.length > 0) {
              Snackbar.show({
                message: 'Some items may be unavailable. Check them on Amazon and try again.',
                severity: 'warning'
              })
            }
          }
        }
      })
    )
  }

  // TODO: Enhance the validation logic to account for the case where
  // a URL that doesn't match the location of the flight is provided in the compact widget
  const isGetOrdersEnabled =
    urls.length > 0 && urls.every((url) => handleUrlValidation(url)) && validateDomainConsistency(urls)

  const getMiniTripDetails = () => {
    if (flightItinerary) {
      return <MiniTripDetails flightItinerary={flightItinerary} />
    }
  }

  const getMarketplaceLinksInput = () => {
    return (
      <UrlWidget
        urls={urls}
        handleInputChange={handleInputChange}
        handleInputKeyPress={handleInputKeyPress}
        removeUrl={removeUrl}
        inputValue={inputValue}
        isValidMarketplaceUrl={handleUrlValidation}
        errors={errors}
        isCompact={isCompact}
        placeholder={isCompact && !isDesktop ? 'Type any item name' : undefined}
        loading={loading}
      />
    )
  }

  const getErrorList = () => {
    if (errors.length > 0) {
      const uniqueErrors = Array.from(new Set(errors.map((error) => error.error.message)))

      return (
        <Box sx={styles.errorListContainer}>
          {uniqueErrors.map((errorMessage, index) => {
            return <Chip key={index} error={{ text: errorMessage, maxLength: 60 }} showCloseIcon={false} />
          })}
        </Box>
      )
    }
  }

  const getButton = () => {
    const buttonText = isCompact ? `Add Item${urls.length > 1 ? 's' : ''}` : 'Proceed'

    return (
      <Button
        text={buttonText}
        buttonType="primary"
        color="primary"
        disabled={!isGetOrdersEnabled}
        onClick={onSubmit}
        loading={loading}
        large={!isCompact && isDesktop}
        style={
          isCompact ? (isDesktop ? styles.compactButton : styles.compactButtonMobile(loading)) : styles.largeButton
        }
        fullWidth={!isDesktop}
      />
    )
  }

  const content = () => {
    if (isCompact) {
      return (
        <Box sx={styles.compactContainer}>
          <Box sx={styles.searchAreaContainer}>
            {getMarketplaceLinksInput()}
            {getButton()}
          </Box>

          {/* {getErrorList()} */}
        </Box>
      )
    } else {
      return (
        <Box sx={styles.largeWidgetContainer}>
          {getMiniTripDetails()}
          {getMarketplaceLinksInput()}

          <Box sx={styles.buttonContainer(errors.length > 0)}>
            {getErrorList()}
            <Box sx={styles.buttonWrapper}>{getButton()}</Box>
          </Box>
        </Box>
      )
    }
  }

  return content()
}
